import { ChangeEvent, useMemo, useState } from "react";
import { faUndo } from "@fortawesome/free-solid-svg-icons/faUndo";
import { AxnuiErrorCodeTypes } from "@iddataweb/idw-common-ui/enums";
import { AXNUIContent, AXNUIContentError } from "@iddataweb/idw-common-ui/types";
import { resources } from "@constants/hardcoded-strings";
import { AXNUIThemingTabTypes } from "@enums/axnui-theming-tab-types";
import { LabelWidthTypes } from "@enums/label-width-types";
import { LayoutTypes } from "@enums/layout-types";
import { PageLevelTypes } from "@enums/page-level-types";
import { ThemingErrorPageValidationErrors } from "@idw-types/common/validation/common/theming-validation-errors";
import { ContentPageBody } from "@containers/common/content-page-body/content-page-body";
import { ContentPageHeader } from "@containers/common/content-page-header/content-page-header";
import { ThemingFormContainer } from "@containers/common/theming-form-container/theming-form-container";
import { ThemingConfigurationContainer } from "@containers/common/theming-configuration-container/theming-configuration-container";
import { ThemingSubHeaderContainer } from "@containers/common/theming-sub-header-container/theming-sub-header-container";
import { ThemingFormSection } from "@containers/common/theming-form-section/theming-form-section";
import { InputFormGroup } from "@containers/common/input-form-group/input-form-group";
import { TextareaFormGroup } from "@containers/common/textarea-form-group/textarea-form-group";
import { ThemingFormGroupWrapper } from "@containers/common/theming-input-form-group/theming-form-group-wrapper";
import { ThemingErrorPageErrorCodeComponent } from "./theming-error-page-error-code-component/theming-error-page-error-code-component";
import { getThemingErrorCodes } from "@utils/content-util";

type ThemingErrorPageTabContainerProps = {
    isDisabled: boolean;
    validationErrors: ThemingErrorPageValidationErrors;
    axnuiContent: AXNUIContent,
    inheritedAXNUIContent: AXNUIContent;
    pageLevel: PageLevelTypes;
    generatePreviewComponent: (currentTab: AXNUIThemingTabTypes) => React.ReactNode;
    updateAXNUIContentPropertyValue: (axnuiContent: AXNUIContent) => void;
};

export const ThemingErrorPageTabContainer = (props: ThemingErrorPageTabContainerProps) => {
    const {
        isDisabled,
        validationErrors,
        axnuiContent,
        inheritedAXNUIContent,
        pageLevel,
        generatePreviewComponent,
        updateAXNUIContentPropertyValue,
    } = props;

    const [labelWidth] = useState(LabelWidthTypes.EXTRA_SMALL);
    const [layout] = useState(LayoutTypes.COLUMN);

    const updateAXNUIContentErrorFieldValue = (key: keyof AXNUIContentError, value: string) => {
        const newPropertyValue: AXNUIContent = {
            ...axnuiContent,
            error: {
                ...axnuiContent.error,
                [key]: value
            }
        };
        updateAXNUIContentPropertyValue(newPropertyValue);
    };

    const handleErrorPageHeaderChange = async (event: ChangeEvent<HTMLInputElement>) => {
        updateAXNUIContentErrorFieldValue("errorTitle", event.target.value);
    };

    const handleResetErrorPageHeader = () => {
        updateAXNUIContentErrorFieldValue("errorTitle", inheritedAXNUIContent.error.errorTitle);
    };

    const handleErrorMessageChange = async (event: ChangeEvent<HTMLInputElement>) => {
        updateAXNUIContentErrorFieldValue("generic", event.target.value);
    };

    const handleResetErrorMessage = () => {
        updateAXNUIContentErrorFieldValue("generic", inheritedAXNUIContent.error.generic);
    };

    const handleErrorDescriptionLabelChange = (event: ChangeEvent<HTMLInputElement>) => {
        updateAXNUIContentErrorFieldValue("errorDescriptionLabel", event.target.value);
    };

    const handleResetErrorDescriptionLabel = () => {
        updateAXNUIContentErrorFieldValue("errorDescriptionLabel", inheritedAXNUIContent.error.errorDescriptionLabel);
    };

    const handleErrorReferenceIdLabelChange = (event: ChangeEvent<HTMLInputElement>) => {
        updateAXNUIContentErrorFieldValue("referenceIdLabel", event.target.value);
    };

    const handleResetErrorReferenceIdLabel = () => {
        updateAXNUIContentErrorFieldValue("referenceIdLabel", inheritedAXNUIContent.error.referenceIdLabel);
    };

    const handleErrorTypeCodeChange = (value: string, errorCodeKey: AxnuiErrorCodeTypes) => {
        updateAXNUIContentErrorFieldValue(errorCodeKey, value);
    };

    const handleResetErrorTypeCode = (errorCodeKey: AxnuiErrorCodeTypes) => {
        updateAXNUIContentErrorFieldValue(errorCodeKey, inheritedAXNUIContent.error[errorCodeKey]);
    }

    const previewComponent = useMemo(() => {
        return generatePreviewComponent(AXNUIThemingTabTypes.ERROR_PAGE);
    }, [generatePreviewComponent]);

    const ErrorCodes = useMemo(() => {
        return getThemingErrorCodes();
    }, []);

    return (
        <div>
            <ContentPageHeader title={resources.pages.verificationServiceDetails.tabs.theming.subTabs.errorPage.title} />
            <ContentPageBody isMaxWidth={true}>
                <ThemingSubHeaderContainer description={resources.pages.verificationServiceDetails.tabs.theming.subTabs.errorPage.description} />
                <ThemingConfigurationContainer>
                    <ThemingFormContainer>
                        <ThemingFormSection>
                            <ThemingFormGroupWrapper
                                inheritedThemingPropertyValue={inheritedAXNUIContent.error.errorTitle}
                                currentThemingPropertyValue={axnuiContent.error.errorTitle}
                                pageLevel={pageLevel}
                            >
                                <InputFormGroup
                                    controlId={"theming-error-page-header-input"}
                                    automationId={"theming-error-page-header-input"}
                                    label={resources.pages.verificationServiceDetails.tabs.theming.subTabs.errorPage.inputs.errorPageHeader.label}
                                    ariaLabel={resources.pages.verificationServiceDetails.tabs.theming.subTabs.errorPage.inputs.errorPageHeader.label}
                                    layout={layout}
                                    labelWidth={labelWidth}
                                    onChange={handleErrorPageHeaderChange}
                                    value={axnuiContent.error.errorTitle || ""}
                                    disabled={isDisabled}
                                    isRequired={true}
                                    afterLabelTooltipText={resources.pages.verificationServiceDetails.tabs.theming.subTabs.errorPage.inputs.errorPageHeader.tooltip}
                                    afterLabelIcon={isDisabled ? null : faUndo}
                                    afterLabelIconOnClick={handleResetErrorPageHeader}
                                />
                            </ThemingFormGroupWrapper>
                            {validationErrors.errorTitleError}

                            <ThemingFormGroupWrapper
                                inheritedThemingPropertyValue={inheritedAXNUIContent.error.generic}
                                currentThemingPropertyValue={axnuiContent.error.generic}
                                pageLevel={pageLevel}
                            >
                                <TextareaFormGroup
                                    controlId={"theming-error-page-error-message-input"}
                                    automationId={"theming-error-page-error-message-input"}
                                    label={resources.pages.verificationServiceDetails.tabs.theming.subTabs.errorPage.inputs.errorMessage.label}
                                    ariaLabel={resources.pages.verificationServiceDetails.tabs.theming.subTabs.errorPage.inputs.errorMessage.label}
                                    layout={layout}
                                    labelWidth={labelWidth}
                                    onChange={handleErrorMessageChange}
                                    value={axnuiContent.error.generic || ""}
                                    disabled={isDisabled}
                                    isRequired={false}
                                    afterLabelTooltipText={resources.pages.verificationServiceDetails.tabs.theming.subTabs.errorPage.inputs.errorMessage.tooltip}
                                    afterLabelIcon={isDisabled ? null : faUndo}
                                    afterLabelIconOnClick={handleResetErrorMessage}
                                />

                            </ThemingFormGroupWrapper>

                            <ThemingFormGroupWrapper
                                inheritedThemingPropertyValue={inheritedAXNUIContent.error.errorDescriptionLabel}
                                currentThemingPropertyValue={axnuiContent.error.errorDescriptionLabel}
                                pageLevel={pageLevel}
                            >
                                <InputFormGroup
                                    controlId={"theming-error-page-error-description-label-input"}
                                    automationId={"theming-error-page-error-description-label-input"}
                                    label={resources.pages.verificationServiceDetails.tabs.theming.subTabs.errorPage.inputs.errorDescriptionLabel.label}
                                    ariaLabel={resources.pages.verificationServiceDetails.tabs.theming.subTabs.errorPage.inputs.errorDescriptionLabel.label}
                                    layout={layout}
                                    labelWidth={labelWidth}
                                    onChange={handleErrorDescriptionLabelChange}
                                    value={axnuiContent.error.errorDescriptionLabel || ""}
                                    disabled={isDisabled}
                                    isRequired={true}
                                    afterLabelTooltipText={""}
                                    afterLabelIcon={isDisabled ? null : faUndo}
                                    afterLabelIconOnClick={handleResetErrorDescriptionLabel}
                                />
                            </ThemingFormGroupWrapper>
                            {validationErrors.errorDescriptionLabelError}

                            <>
                                {ErrorCodes.map((errorCode: AxnuiErrorCodeTypes, index) =>
                                    <ThemingFormGroupWrapper
                                        key={`error-code-${index}`}
                                        inheritedThemingPropertyValue={inheritedAXNUIContent.error[errorCode]}
                                        currentThemingPropertyValue={axnuiContent.error[errorCode]}
                                        pageLevel={pageLevel}
                                    >
                                        <ThemingErrorPageErrorCodeComponent
                                            errorCode={errorCode}
                                            isDisabled={isDisabled}
                                            axnuiContentError={axnuiContent.error}
                                            onChange={handleErrorTypeCodeChange}
                                            onReset={handleResetErrorTypeCode}
                                            validationError={validationErrors.errorCodesErrors[errorCode]}
                                        />
                                    </ThemingFormGroupWrapper>)
                                }
                            </>

                            <ThemingFormGroupWrapper
                                inheritedThemingPropertyValue={inheritedAXNUIContent.error.referenceIdLabel}
                                currentThemingPropertyValue={axnuiContent.error.referenceIdLabel}
                                pageLevel={pageLevel}
                            >
                                <TextareaFormGroup
                                    controlId={"theming-error-page-error-reference-id-label-input"}
                                    automationId={"theming-error-page-error-reference-id-label-input"}
                                    label={resources.pages.verificationServiceDetails.tabs.theming.subTabs.errorPage.inputs.referenceIdLabel.label}
                                    ariaLabel={resources.pages.verificationServiceDetails.tabs.theming.subTabs.errorPage.inputs.referenceIdLabel.label}
                                    layout={layout}
                                    labelWidth={labelWidth}
                                    onChange={handleErrorReferenceIdLabelChange}
                                    value={axnuiContent.error.referenceIdLabel || ""}
                                    disabled={isDisabled}
                                    isRequired={true}
                                    afterLabelTooltipText={""}
                                    afterLabelIcon={isDisabled ? null : faUndo}
                                    afterLabelIconOnClick={handleResetErrorReferenceIdLabel}
                                />
                            </ThemingFormGroupWrapper>
                            {validationErrors.referenceIdLabel}
                        </ThemingFormSection>
                    </ThemingFormContainer>
                    {previewComponent}
                </ThemingConfigurationContainer>
            </ContentPageBody>
        </div >
    );
};